import { Link, useParams } from "react-router-dom";
import { getDramaById, getActorsByIds } from "@/lib/data";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import EpisodeGuide from "@/pages/detail/components/EpisodeGuide";
import CastList from "@/pages/detail/components/CastList";
import RelatedDramas from "@/pages/detail/components/RelatedDramas";
import DetailActions from "@/pages/detail/components/DetailActions";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function DramaDetail() {
  const { id } = useParams<{ id: string }>();
  const drama = id ? getDramaById(id) : undefined;

  usePageMeta(
    drama ? `${drama.title} 详情介绍 - 漂亮的保姆` : "剧集详情 - 漂亮的保姆",
    drama
      ? `${drama.title}（${drama.year}）剧情介绍与演员信息。${drama.synopsis.slice(0, 60)}……在漂亮的保姆查看完整剧情、分集解析与演员阵容。`
      : "在漂亮的保姆查看剧集详情、剧情介绍与演员阵容。",
    drama
      ? `${drama.title},${drama.genre},剧情介绍,演员表,漂亮的保姆,${drama.tags.join(",")}`
      : "漂亮的保姆,剧集详情,剧情介绍",
  );

  if (!drama) {
    return (
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-16">
        <EmptyState
          title="未找到该剧集"
          desc="你访问的剧集可能已下线，可以返回首页或浏览其他剧集。"
          actionText="返回首页"
          onAction={() => window.REACT_APP_NAVIGATE("/")}
        />
      </div>
    );
  }

  const cast = getActorsByIds(drama.castIds);

  return (
    <>
      <div className="bg-primary-900">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6 md:py-8">
          <div className="[&_a]:text-background-300 [&_span]:text-background-200 [&_.text-foreground-900]:text-background-50 [&_.ri-arrow-right-s-line]:text-primary-600">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "剧集分类", to: `/category/${drama.type}` },
                { label: drama.title },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-5 md:gap-6">
              <div className="w-40 sm:w-full mx-auto sm:mx-0">
                <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-200">
                  <img
                    src={drama.cover}
                    alt={drama.title}
                    title={`${drama.title} 剧集封面 - 漂亮的保姆`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {drama.title}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium">
                    {drama.genre}
                  </span>
                  <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-700 text-xs font-medium">
                    {drama.rating.toFixed(1)} 分
                  </span>
                  <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs font-medium">
                    {drama.status}
                  </span>
                </div>
                <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div className="flex gap-2">
                    <dt className="text-foreground-400 shrink-0">年份</dt>
                    <dd className="text-foreground-800">{drama.year} 年</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-400 shrink-0">地区</dt>
                    <dd className="text-foreground-800">{drama.region}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-400 shrink-0">集数</dt>
                    <dd className="text-foreground-800">共 {drama.episodes} 集</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-400 shrink-0">导演</dt>
                    <dd className="text-foreground-800">{drama.director}</dd>
                  </div>
                  <div className="flex gap-2 sm:col-span-2">
                    <dt className="text-foreground-400 shrink-0">主演</dt>
                    <dd className="text-foreground-800 flex flex-wrap gap-1.5">
                      {cast.map((a) => (
                        <Link
                          key={a.id}
                          to={`/actor/${a.id}`}
                          className="hover:text-primary-600 transition-colors cursor-pointer"
                        >
                          {a.name}
                        </Link>
                      ))}
                    </dd>
                  </div>
                </dl>
                <div className="mt-4 flex flex-wrap gap-2">
                  {drama.tags.map((t) => (
                    <Link
                      key={t}
                      to={`/search?q=${encodeURIComponent(t)}`}
                      className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-background-200 transition-colors cursor-pointer"
                    >
                      #{t}
                    </Link>
                  ))}
                </div>
                <div className="mt-5">
                  <DetailActions dramaId={drama.id} />
                </div>
              </div>
            </div>

            <section className="mt-8 bg-background-50 rounded-lg border border-background-200 p-4 md:p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1.5 h-6 rounded-full bg-primary-500" />
                <h2 className="font-heading text-xl font-bold text-foreground-950">
                  剧情简介
                </h2>
              </div>
              <p className="text-sm md:text-base text-foreground-600 leading-loose">
                {drama.synopsis}
              </p>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5 lg:sticky lg:top-24">
              <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">
                剧集信息
              </h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center justify-between">
                  <span className="text-foreground-400">评分</span>
                  <span className="text-accent-600 font-semibold">
                    {drama.rating.toFixed(1)} / 10
                  </span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-400">热度</span>
                  <span className="text-foreground-800">{drama.popularity}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-400">最近更新</span>
                  <span className="text-foreground-800">{drama.updatedAt}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-400">状态</span>
                  <span className="text-foreground-800">{drama.status}</span>
                </li>
              </ul>
              <Link
                to={`/category/${drama.type}`}
                className="mt-5 flex items-center justify-center gap-2 h-10 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-apps-2-line" />
                浏览同类剧集
              </Link>
            </div>
          </aside>
        </div>

        <div className="mt-8 space-y-8">
          {drama.episodeGuide.length > 0 && <EpisodeGuide guides={drama.episodeGuide} />}
          <CastList castIds={drama.castIds} />
          <RelatedDramas drama={drama} />
        </div>
      </div>
    </>
  );
}